/**
 * ProjectSelector — ön muhasebe belge ve para hareketi formlarında kullanılan
 * ortak "Proje (opsiyonel)" autocomplete alanı.
 *
 * Öneri listesinde yalnız aktif projeler görünür; düzenlenen kayıt tamamlanmış
 * veya iptal edilmiş bir projeye bağlıysa o proje de (durum etiketiyle) gösterilir,
 * böylece mevcut bağ kazara kaybolmaz.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, Chip, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  getProjectsPaged,
  getProjectById,
  type Project,
} from '../../../services/projectService';

interface Props {
  /** Seçili proje kimliği; bağ yoksa null. */
  value: number | null;
  onChange: (projectId: number | null) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  /** Varsayılan: "Proje (opsiyonel)" (i18n). */
  label?: string;
  /** Alanı tam genişlik yapar (varsayılan true). */
  fullWidth?: boolean;
}

const PAGE_SIZE = 50;

const ProjectSelector: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  size = 'small',
  label,
  fullWidth = true,
}) => {
  const { t } = useTranslation('common2');

  const [options, setOptions] = useState<Project[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  /** Seçili proje öneri listesinde yoksa (pasif proje) ayrıca tutulur. */
  const [selectedFallback, setSelectedFallback] = useState<Project | null>(null);

  const loadOptions = useCallback(async (term: string) => {
    try {
      setLoading(true);
      const result = await getProjectsPaged({
        page: 1,
        pageSize: PAGE_SIZE,
        search: term || undefined,
        status: 'active',
      });
      setOptions(result.data);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (disabled) return;
    const handle = setTimeout(() => loadOptions(search), search ? 300 : 0);
    return () => clearTimeout(handle);
  }, [search, disabled, loadOptions]);

  // Düzenleme modunda pasif/tamamlanmış projeye bağlı kayıt: projeyi tek tek getir
  useEffect(() => {
    if (!value) {
      setSelectedFallback(null);
      return;
    }
    if (options.some((p) => p.id === value) || selectedFallback?.id === value) return;

    let cancelled = false;
    getProjectById(value)
      .then((project) => {
        if (!cancelled) setSelectedFallback(project);
      })
      .catch(() => {
        /* proje silinmiş olabilir — alan boş görünür, bağ korunur */
      });
    return () => {
      cancelled = true;
    };
  }, [value, options, selectedFallback]);

  const allOptions = selectedFallback && !options.some((p) => p.id === selectedFallback.id)
    ? [selectedFallback, ...options]
    : options;

  const selected = allOptions.find((p) => p.id === value) ?? null;

  const statusLabel = (status: string) => {
    if (status === 'completed') return t('projects.statusCompleted');
    if (status === 'cancelled') return t('projects.statusCancelled');
    return null;
  };

  /** Aktif olmayan projeye bağlı kayıtta uyarı gösterilir; bağ yine korunur. */
  const inactiveLabel = selected ? statusLabel(selected.status) : null;

  return (
    <Autocomplete<Project>
      options={allOptions}
      value={selected}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      onChange={(_, option) => onChange(option ? option.id : null)}
      onInputChange={(_, newValue, reason) => {
        if (reason === 'input') setSearch(newValue);
        if (reason === 'clear') setSearch('');
      }}
      getOptionLabel={(option) => (option.code ? `${option.name} (${option.code})` : option.name)}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      noOptionsText={t('projects.selector.noOptions')}
      renderOption={(props, option) => {
        const badge = statusLabel(option.status);
        return (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box sx={{ flexGrow: 1 }}>
                {option.name}
                {option.code && ` (${option.code})`}
              </Box>
              {badge && <Chip size="small" variant="outlined" label={badge} />}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('projects.selector.label')}
          placeholder={t('projects.selector.placeholder')}
          helperText={
            inactiveLabel
              ? t('projects.selector.inactiveWarning', { status: inactiveLabel })
              : undefined
          }
          FormHelperTextProps={inactiveLabel ? { sx: { color: 'warning.main' } } : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProjectSelector;
